import axios from 'axios';
import { API_BASE_URL } from '../config/urlConfig';

// Create axios instance with auth token
const getApiClient = () => {
  const token = localStorage.getItem('token');

  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    },
  });
};

/**
 * Shorten a URL
 * @param {string} originalUrl - The URL to shorten
 * @param {string} customCode - Optional custom short code
 * @returns {Promise} - API response
 */
export const shortenUrl = async (originalUrl, customCode = null) => {
  try {
    const apiClient = getApiClient();
    const payload = { originalUrl };

    if (customCode) {
      payload.customCode = customCode;
    }

    const response = await apiClient.post('/api/urls/shorten', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Get statistics for a shortened URL
 * @param {string} shortCode - The short code to get stats for
 * @returns {Promise} - API response
 */
export const getUrlStats = async (shortCode) => {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get(`/api/urls/stats/${shortCode}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Get a list of all URLs
 * @returns {Promise} - API response with all URLs
 */
export const getAllUrls = async () => {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get('/api/urls');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

/**
 * Update a shortened URL's target
 * @param {string} shortCode - The short code of the URL to update
 * @param {string} originalUrl - The new target URL
 * @returns {Promise} - API response
 */
export const updateUrl = async (shortCode, originalUrl) => {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.put(`/api/urls/${shortCode}`, { originalUrl });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Create a properly named export object
const urlService = {
  shortenUrl,
  getUrlStats,
  getAllUrls,
  updateUrl
};

export default urlService;
